import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, Paper, CircularProgress, Alert, Stack,
    FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './paymentTypesApi';
import type { PaymentType, Account } from './paymentTypesApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    paymentTypeId: number | null;
}

export function PaymentTypeView({ paymentTypeId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<PaymentType | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [copy, setCopy] = useState<PaymentType | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const accountsData = await api.getAccounts();
            setAccounts(accountsData);
            if (paymentTypeId != null) {
                const data = await api.getPaymentType(paymentTypeId);
                if (!data) throw new Error(`Payment type ${paymentTypeId} returned empty response`);
                setItem(data);
                setIsEditing(false);
            }
        } catch (err) {
            console.error('PaymentTypeView load failed:', err);
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [paymentTypeId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return <Alert severity="info">No payment type selected.</Alert>;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = await api.updatePaymentType(item);
            setItem(response);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.paymentTransactionCode ?? ''} ${item.paymentTransactionTypeName ?? ''}`}
                isEditing={isEditing}
                isNew={false}
                saving={saving}
                canSave
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('paymenttype.paymenttypecode')}>
                        <Typography sx={sx14}>{item.paymentTransactionCode}</Typography>
                    </FormRow>

                    <FormRow label={t('paymenttype.paymenttypename')}>
                        <Typography sx={sx14}>{item.paymentTransactionTypeName}</Typography>
                    </FormRow>

                    <FormRow label={t('paymenttype.account')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>
                                {item.account ? `${item.account.accountNumber} ${item.account.accountName}` : ''}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.account?.id ?? ''}
                                    onChange={e => {
                                        const a = accounts.find(x => x.id === e.target.value);
                                        setItem({ ...item, account: a ?? null });
                                    }}
                                >
                                    {[...accounts]
                                        .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                                        .map(a => (
                                            <MenuItem key={a.id} value={a.id}>{a.accountNumber} {a.accountName}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
